import { ChangeDetectionStrategy, Component, computed, inject, input, InputSignal, Signal } from '@angular/core';

import { MapLayerSource } from '@connect-services/client/components/map/map.component';
import { SourceItemComponent } from '@connect-services/client/components/sources/source-item/source-item.component';
import {
    LayerVisibilityButtonState,
    VisibilityButtonComponent,
} from '@connect-services/client/components/visibility-button/visibility-button.component';
import { RoutePathEnum } from '@connect-services/client/models/routes.model';
import { BroadcastChannelService } from '@connect-services/client/services/broadcast-channel.service';
import { MapDrawService } from '@connect-services/client/services/map-draw.service';
import { SourcesActivationService } from '@connect-services/client/services/source-activation.service';
import { SourcesVisibilityService, SourceTypeEnum } from '@connect-services/client/services/sources-visibility.service';
import { ExpansionPanelComponent } from '@connect-services/src/lib/components/expansion-panel/expansion-panel.component';
import { LayerTopic } from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ExpansionPanelComponent, RessourcesModule, SourceItemComponent, VisibilityButtonComponent],
    selector: 'app-source-theme',
    standalone: true,
    templateUrl: './source-theme.component.html',
})
export class SourceThemeComponent {
    public readonly areLayersGrouped: InputSignal<boolean> = input.required<boolean>();
    public readonly canBeEdited = input<boolean>(true);
    public readonly groupedSources: InputSignal<MapLayerSource[][]> = input.required<MapLayerSource[][]>();
    public readonly hasTabView = input<boolean>(true);
    public readonly theme: InputSignal<LayerTopic> = input.required<LayerTopic>();
    public readonly visibilityButtonState = computed<LayerVisibilityButtonState>(() => {
        if (!this.flatSources().length) {
            return LayerVisibilityButtonState.HiddenDisabled;
        }

        const anyVisible = this.flatSources().some((source: MapLayerSource) =>
            this.sourcesVisibilityService.isVisible(source),
        );

        const anyInZoomRange = this.sourcesVisibilityService.isAnySourceInZoomRange(this.flatSources());

        return this.sourcesVisibilityService.mapVisibilityButtonState(true, anyVisible, anyInZoomRange);
    });

    protected readonly isThemeActive: Signal<boolean> = computed(() =>
        this.flatSources().some((source: MapLayerSource) => this.sourcesActivationService.get(source.sourceId)),
    );
    protected readonly isThemeVisible = computed(() =>
        this.flatSources().some((source: MapLayerSource) => this.sourcesVisibilityService.isVisible(source)),
    );
    protected readonly mapDrawService = inject(MapDrawService);
    protected readonly sourcesActivationService = inject(SourcesActivationService);
    protected readonly sourcesVisibilityService = inject(SourcesVisibilityService);
    protected readonly SourceTypeEnum = SourceTypeEnum;
    protected readonly themeIcon = computed(() => {
        return this.theme()?.icon ?? 'layer-group';
    });

    private readonly broadcastChannelService = inject(BroadcastChannelService);
    private readonly flatSources = computed<MapLayerSource[]>(() => this.groupedSources().flat());

    protected changeThemeSourcesVisibility(event: MouseEvent): void {
        event.stopPropagation();

        if (!this.flatSources().length) {
            return;
        }

        this.sourcesVisibilityService.changeSourceVisibility(this.flatSources(), !this.isThemeVisible());
    }

    protected onVisibilityToggle(event: MouseEvent): void {
        this.changeThemeSourcesVisibility(event);
    }

    protected openLayerInExternalWindow(event: MouseEvent, layerName: string): void {
        event.stopPropagation();

        const url = document.URL;
        const pathSuffix = `/${RoutePathEnum.LAYERS}`;
        const finalURL = url.slice(0, url.length - pathSuffix.length);

        this.broadcastChannelService.openInNewWindow(finalURL, {
            ext: 'true',
            layerName: layerName,
        });
    }
}
